import React from "react"

import { format } from "date-fns"
import { CalendarDays, Clock, MapPin } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { cn } from "@/lib/utils"

interface TimeSlot {
  time: string
  fullDate: Date
}

interface DateTimeSelectorProps {
  availableDates: Date[]
  selectedDate: Date | undefined
  onDateSelect: (date: Date | undefined) => void
  onTimeSelect: (fullDate: Date) => void
  title: string
  locationText: string
}

const DateTimeSelector = ({
  availableDates,
  selectedDate,
  onDateSelect,
  onTimeSelect,
  title,
  locationText,
}: DateTimeSelectorProps) => {
  const isDateAvailable = (date: Date) => {
    return availableDates.some(
      (availableDate) =>
        availableDate.getDate() === date.getDate() &&
        availableDate.getMonth() === date.getMonth() &&
        availableDate.getFullYear() === date.getFullYear(),
    )
  }

  const availableTimesForDate = (date: Date): TimeSlot[] => {
    if (!date) return []
    return availableDates
      .filter(
        (availableDate) =>
          availableDate.getDate() === date.getDate() &&
          availableDate.getMonth() === date.getMonth() &&
          availableDate.getFullYear() === date.getFullYear(),
      )
      .map((d) => ({
        // Extract hours and minutes directly from the Date object without timezone conversion
        time: `${d.getHours() % 12 || 12}:${d
          .getMinutes()
          .toString()
          .padStart(2, "0")} ${d.getHours() >= 12 ? "PM" : "AM"}`,
        fullDate: d,
      }))
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendar Section */}
        <Card className="shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <CalendarDays className="w-5 h-5 text-honeycomb" />
              Choose Your Date
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center">
              <Calendar
                mode="single"
                weekStartsOn={1}
                selected={selectedDate}
                onSelect={onDateSelect}
                disabled={(date) => !isDateAvailable(date) || date < new Date()}
                className="rounded-md border-0"
              />
            </div>
          </CardContent>
        </Card>

        {/* Time Selection */}
        <Card className="shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5 text-honeycomb" />
              Available Times
            </CardTitle>
          </CardHeader>
          <CardContent className="min-h-[300px]">
            {selectedDate ? (
              availableTimesForDate(selectedDate).length > 0 ? (
                <div className="space-y-3">
                  <p className="text-sm text-palette-jungleGreen600 mb-4">
                    {format(selectedDate, "EEEE, MMMM d, yyyy")}
                  </p>
                  <div className="grid grid-cols-1 gap-2">
                    {availableTimesForDate(selectedDate).map((timeSlot, index) => (
                      <Button
                        key={index}
                        type="button"
                        variant={
                          timeSlot.fullDate.getTime() === selectedDate?.getTime()
                            ? "default"
                            : "outline"
                        }
                        className={cn(
                          "w-full justify-start h-12 text-left",
                          timeSlot.fullDate.getTime() === selectedDate?.getTime()
                            ? "bg-primary hover:bg-amber-500 text-white"
                            : "hover:bg-palette-jungleGreen000",
                        )}
                        onClick={() => onTimeSelect(timeSlot.fullDate)}
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        {timeSlot.time}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <Clock className="w-12 h-12 text-palette-jungleGreen300 mb-3" />
                  <p className="text-palette-jungleGreen500">No times available for this date</p>
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <CalendarDays className="w-12 h-12 text-palette-jungleGreen300 mb-3" />
                <p className="text-palette-jungleGreen500">Please select a date first</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Confirmation Summary */}
      {selectedDate &&
        availableTimesForDate(selectedDate).some(
          (t) => t.fullDate.getTime() === selectedDate.getTime(),
        ) && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 text-green-600" />
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold text-green-900">{title} Confirmed</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-green-800">Date & Time:</span>
                      <p className="text-green-700">
                        {format(selectedDate, "EEEE, MMMM d, yyyy")} at{" "}
                        {format(selectedDate, "h:mm a")}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-green-800 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        Location:
                      </span>
                      <p className="text-green-700">{locationText}</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
    </div>
  )
}

export default DateTimeSelector
